import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { RefreshCw, DollarSign } from 'lucide-react'
import { useSales, useRefreshSales } from '../hooks/useSales'
import { SalesTable } from '../components/SalesTable'
import { ExportSalesDialog } from '../components/ExportSalesDialog'
import { EmptyState } from '@/components/common/data-display/EmptyState'
import { LoadingSpinner } from '@/components/common/feedback/LoadingSpinner'
import { Pagination } from '@/components/common/navigation/Pagination'
import { formatCurrency } from '@/lib/utils'
import { PAGINATION } from '@/config/constants'
import { cn } from '@/lib/utils'

export function SalesPage() {
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState<number>(PAGINATION.DEFAULT_PAGE_SIZE)
  const navigate = useNavigate()

  const { data, isLoading, refetch } = useSales({
    start: (page - 1) * pageSize,
    limit: pageSize,
  })

  const { mutate: refreshSales, isPending: isRefreshing } = useRefreshSales()

  const handleRefresh = async () => {
    await refreshSales()
    refetch()
  }

  // Both of these sum the CURRENT PAGE, not the whole history — the API returns one page and a
  // total count, never every row. Labelled as such below rather than presented as lifetime
  // figures, which is what they looked like sitting beside "Total Sales".
  const pageCommission = data?.data.reduce((sum, sale) => sum + (Number(sale.commission) || 0), 0) || 0
  const pageItems = data?.data.reduce((sum, s) => sum + (Number(s.productQuantity) || 0), 0) || 0

  return (
    <div className="space-y-8 p-8">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div className="space-y-1">
          <h1 className="font-serif text-4xl font-semibold tracking-tight">My Sales</h1>
          <p className="text-muted-foreground text-base">Track your sales and commissions</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleRefresh} disabled={isRefreshing} className="shadow-subtle">
            <RefreshCw className={cn('mr-2 h-4 w-4', isRefreshing && 'animate-spin')} />
            Refresh
          </Button>
          <ExportSalesDialog />
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <LoadingSpinner size="lg" />
        </div>
      ) : !data || data.data.length === 0 ? (
        <EmptyState
          title="No sales yet"
          description="Your sales will appear here once orders are placed"
          icon={DollarSign}
        />
      ) : (
        <>
          {/* Summary Card */}
          <div className="grid gap-5 md:grid-cols-3">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium font-sans text-muted-foreground">
                  Commission (this page)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-4xl font-serif font-semibold tracking-tight text-green-600">
                  {formatCurrency(pageCommission)}
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium font-sans text-muted-foreground">
                  Sales (all time)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-4xl font-serif font-semibold tracking-tight tabular-nums">
                  {data.total.toLocaleString()}
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium font-sans text-muted-foreground">
                  Items (this page)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-4xl font-serif font-semibold tracking-tight">
                  {pageItems}
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Sales Table */}
          <Card>
            <CardContent className="pt-6">
              <SalesTable
                sales={data.data}
                onRowClick={(sale) => navigate(`/app/artworks/${sale.artworkId}`)}
              />

              {data.total > pageSize && (
                <div className="mt-6">
                  <Pagination
                    page={page}
                    pageSize={pageSize}
                    total={data.total}
                    onPageChange={setPage}
                    onPageSizeChange={setPageSize}
                  />
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  )
}

